/**
 * Render Tiptap passage with ___ blanks replaced by interactive widgets
 */

import * as React from 'react';
import type { TiptapJSON } from '@ai-content-hub/content-kit';
import map from 'lodash/map';
import { FITB_BLANK_MARKER_REGEX } from './schema';
import { PassageImage, PassageAudio, PassageEmbed } from './PassageMedia';

type MarkAttrPrimitive = string | number | boolean;

type Mark = { type: string; attrs?: Record<string, MarkAttrPrimitive> };

/** Common Tiptap node attrs used when rendering FITB passages. */
type PassageNodeAttrs = {
    textAlign?: string;
    level?: number;
    src?: string;
    alt?: string;
    title?: string;
    width?: number | string | null;
    height?: number | null;
    // EAA-1736: media layout attrs so passage media matches the editor.
    layout?: string;
    x?: number | null;
    y?: number | null;
};

function passageNodeAttrs(node: TiptapJSON): PassageNodeAttrs | undefined {
    if (!('attrs' in node)) return undefined;
    const raw = (node as { attrs?: PassageNodeAttrs }).attrs;
    return raw;
}

function paragraphTextAlignStyle(attrs: PassageNodeAttrs | undefined): React.CSSProperties | undefined {
    const ta = attrs?.textAlign;
    if (typeof ta !== 'string' || ta === '') {
        return undefined;
    }
    return { textAlign: ta as React.CSSProperties['textAlign'] };
}

export interface PassageRenderContext {
    renderBlank: (blankIndex: number, blankId: string) => React.ReactNode;
}

function splitTextParts(text: string): Array<{ kind: 'text'; text: string } | { kind: 'blank' }> {
    const parts: Array<{ kind: 'text'; text: string } | { kind: 'blank' }> = [];
    const re = new RegExp(FITB_BLANK_MARKER_REGEX.source, 'g');
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push({ kind: 'text', text: text.slice(last, m.index) });
        parts.push({ kind: 'blank' });
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
    if (parts.length === 0 && text.length > 0) parts.push({ kind: 'text', text });
    return parts;
}

function wrapSingleMark(node: React.ReactNode, mark: Mark, key: string): React.ReactNode {
    switch (mark.type) {
        case 'bold':
            return <strong key={key}>{node}</strong>;
        case 'italic':
            return <em key={key}>{node}</em>;
        case 'underline':
            return <u key={key}>{node}</u>;
        case 'strike':
            return <s key={key}>{node}</s>;
        case 'code':
            return (
                <code key={key} className="rounded-sm bg-muted px-1 font-mono text-sm">
                    {node}
                </code>
            );
        case 'link':
            return (
                <a
                    key={key}
                    href={String(mark.attrs?.href ?? '#')}
                    className="text-primary underline"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {node}
                </a>
            );
        case 'highlight':
            return (
                <mark key={key} className="bg-warning/30">
                    {node}
                </mark>
            );
        case 'textStyle':
            if (mark.attrs?.color) {
                return (
                    <span key={key} style={{ color: String(mark.attrs.color) }}>
                        {node}
                    </span>
                );
            }
            return <React.Fragment key={key}>{node}</React.Fragment>;
        case 'subscript':
            return <sub key={key}>{node}</sub>;
        case 'superscript':
            return <sup key={key}>{node}</sup>;
        default:
            return <React.Fragment key={key}>{node}</React.Fragment>;
    }
}

function applyMarks(text: string, marks?: Mark[]): React.ReactNode {
    let node: React.ReactNode = text;
    if (!marks?.length) return node;
    for (let i = marks.length - 1; i >= 0; i--) {
        node = wrapSingleMark(node, marks[i], `m-${i}`);
    }
    return node;
}

export function renderPassageWithBlanks(
    doc: TiptapJSON | undefined,
    ctx: PassageRenderContext
): React.ReactNode {
    if (!doc?.content) return null;

    const blankState = { seq: 0 };

    const renderInline = (
        nodes: TiptapJSON[] | undefined,
        keyPrefix: string
    ): React.ReactNode[] => {
        if (!nodes) return [];
        const out: React.ReactNode[] = [];
        map(nodes, (child, idx) => {
            if (child.type === 'text' && typeof child.text === 'string') {
                const parts = splitTextParts(child.text);
                map(parts, (part, pIdx) => {
                    if (part.kind === 'text') {
                        if (part.text) {
                            out.push(
                                <span key={`${keyPrefix}-t-${idx}-${pIdx}`}>
                                    {applyMarks(part.text, child.marks as Mark[])}
                                </span>
                            );
                        }
                    } else {
                        const bi = blankState.seq;
                        blankState.seq += 1;
                        const id = `blank-${bi}`;
                        out.push(
                            <span
                                key={`${keyPrefix}-b-${idx}-${pIdx}`}
                                className="fitb-passage-blank-slot inline"
                            >
                                {ctx.renderBlank(bi, id)}
                            </span>
                        );
                    }
                });
            } else if (child.type === 'hardBreak') {
                out.push(<br key={`${keyPrefix}-br-${idx}`} />);
            } else {
                const nested = mapBlock(child, `${keyPrefix}-n-${idx}`);
                if (nested) out.push(nested);
            }
        });
        return out;
    };

    const mapBlock = (node: TiptapJSON, keyBase: string): React.ReactNode | null => {
        switch (node.type) {
            case 'paragraph':
                return (
                    <p
                        key={keyBase}
                        className="fitb-passage-p mb-2 last:mb-0"
                        style={paragraphTextAlignStyle(passageNodeAttrs(node))}
                    >
                        {renderInline(node.content, keyBase)}
                    </p>
                );
            case 'heading': {
                const attrs = passageNodeAttrs(node);
                const level = Math.min(Number(attrs?.level) || 1, 4);
                const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4';
                return (
                    <Tag
                        key={keyBase}
                        className="fitb-passage-heading font-bold mb-2"
                        style={paragraphTextAlignStyle(attrs)}
                    >
                        {renderInline(node.content, keyBase)}
                    </Tag>
                );
            }
            case 'bulletList':
                return (
                    <ul key={keyBase} className="fitb-passage-ul list-disc pl-6 my-2 space-y-1">
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-li-${i}`))}
                    </ul>
                );
            case 'orderedList':
                return (
                    <ol key={keyBase} className="fitb-passage-ol list-decimal pl-6 my-2 space-y-1">
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-li-${i}`))}
                    </ol>
                );
            case 'listItem':
                return (
                    <li key={keyBase} className="fitb-passage-li">
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-c-${i}`))}
                    </li>
                );
            case 'blockquote':
                return (
                    <blockquote
                        key={keyBase}
                        className="fitb-passage-quote border-l-4 border-primary/40 pl-4 my-2 text-muted-foreground italic"
                    >
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-q-${i}`))}
                    </blockquote>
                );
            case 'horizontalRule':
                return <hr key={keyBase} className="fitb-passage-hr my-4 border-border" />;
            case 'image': {
                // EAA-1736: resolve storage srcs + honor layout (was raw <img>).
                const imgAttrs = passageNodeAttrs(node);
                if (!imgAttrs?.src) return null;
                return <PassageImage key={keyBase} attrs={imgAttrs} />;
            }
            case 'audio': {
                // EAA-1736: audio was dropped by the player (fell to default).
                const audioAttrs = passageNodeAttrs(node);
                if (!audioAttrs?.src) return null;
                return <PassageAudio key={keyBase} attrs={audioAttrs} />;
            }
            case 'embed': {
                // EAA-1736: embed was dropped by the player (fell to default).
                const embedAttrs = passageNodeAttrs(node);
                if (!embedAttrs?.src) return null;
                return <PassageEmbed key={keyBase} attrs={embedAttrs} />;
            }
            case 'codeBlock': {
                const text = extractPlainTextFromContent(node.content);
                return (
                    <pre
                        key={keyBase}
                        className="fitb-passage-pre rounded-md bg-muted/50 border border-border p-3 overflow-x-auto text-sm my-2"
                    >
                        <code>{text}</code>
                    </pre>
                );
            }
            case 'table':
                return (
                    <table
                        key={keyBase}
                        className="fitb-passage-table w-full border-collapse border border-border my-3 table-fixed"
                    >
                        <tbody>
                            {map(node.content, (c, i) => mapBlock(c, `${keyBase}-r-${i}`))}
                        </tbody>
                    </table>
                );
            case 'tableRow':
                return (
                    <tr key={keyBase}>
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-c-${i}`))}
                    </tr>
                );
            case 'tableHeader':
                return (
                    <th
                        key={keyBase}
                        className="fitb-passage-th border border-border p-2 bg-muted/40 font-semibold align-top text-start"
                    >
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-h-${i}`))}
                    </th>
                );
            case 'tableCell':
                return (
                    <td
                        key={keyBase}
                        className="fitb-passage-td border border-border p-2 align-top text-start"
                    >
                        {map(node.content, (c, i) => mapBlock(c, `${keyBase}-d-${i}`))}
                    </td>
                );
            default:
                if (node.content && Array.isArray(node.content)) {
                    return (
                        <div key={keyBase} className="fitb-passage-unknown my-1">
                            {map(node.content, (c, i) => mapBlock(c, `${keyBase}-u-${i}`))}
                        </div>
                    );
                }
                return null;
        }
    };

    return (
        // EAA-1736: relative + isolate so behind/front overlay media anchor here.
        <div
            className="fitb-passage-root text-foreground space-y-1"
            style={{ position: 'relative', isolation: 'isolate' }}
        >
            {map(doc.content, (child, i) => mapBlock(child, `root-${i}`))}
        </div>
    );
}

function extractPlainTextFromContent(content: TiptapJSON[] | undefined): string {
    if (!content) return '';
    const parts: string[] = [];
    const walk = (nodes: TiptapJSON[]) => {
        map(nodes, (n) => {
            if (n.type === 'text' && typeof n.text === 'string') parts.push(n.text);
            if (n.content) walk(n.content);
        });
    };
    walk(content);
    return parts.join('\n');
}
